import React from 'react'
import styled from 'styled-components'
import { Flex, Box } from 'reflexbox/styled-components'
import PropTypes from 'prop-types'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'

import { H3 } from 'lib/ui/primitives'

import IconCheck from '@anvilco-pkg/ui/components/Icon/IconCheck'

import useAnvilUser from 'hooks/useAnvilUser'

import VideoModal from 'components/VideoModal'
import SubSection from 'components/page/Section/SubSection'
import Button from 'components/Button'
import PlayButton from 'components/PlayButton'

import media from 'media'
import theme from 'theme'

import { APP_URL } from 'helpers'

const HeroContent = styled(Flex).attrs({
  flexDirection: ['column', 'column', 'column', 'row'],
})`
  gap: 24px;

  ${media.giant`
    gap: 50px;
  `}

  ${media.beyondGiant`
    gap: 110px;
  `}
`

const TextContainer = styled(Box)
  .attrs(({ noCover }) => ({
    maxWidth: ['100%', '100%', '100%', noCover ? 700 : 365],
    textAlign: [noCover ? 'center' : 'left'],
    mx: ['auto'],
    pt: [0, 0, 0, noCover ? 0 : 40],
  }))
  .withConfig({
    shouldForwardProp: (prop) => ['noCover'].indexOf(prop) === -1,
  })``

const AssetContainer = styled(Box).attrs({ flex: 1 })``

const Title = styled(H3).attrs({ as: 'h1', mb: 16 })``

const Description = styled(Box).attrs({ mb: 32 })``

const CtaWrapper = styled(Box).attrs({ mb: 12 })``

const Cta = styled(Button)``

const VideoAssetContainer = styled(Box).attrs({
  height: ['auto', 'auto', 'auto', 355],
})`
  position: relative;
  cursor: pointer;
  width: 100%;
`

const VideoAssetInner = styled(Box)`
  position: relative;
  width: 100%;
  height: 100%;
`

const StyledVideoAssetPlaceholderImg = styled(GatsbyImage)`
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 1;
`

const PlanContainer = styled(Flex)
  .withConfig({
    shouldForwardProp: (prop) => ['isActive'].indexOf(prop) === -1,
  })
  .attrs({
    fontSize: 12,
    fontWeight: theme.fontWeights.bold,
    height: 24,
    alignItems: 'center',
    px: '10px',
  })`
    background-color: ${({ isActive }) => (isActive ? theme.colors.white : 'transparent')};
    border: 1px solid ${({ isActive }) => (isActive ? theme.colors.black : theme.colors.blacks[40])};
    color: ${({ isActive }) => (isActive ? theme.colors.black : theme.colors.blacks[40])};
    border-radius: 100px;
  `

const StyledIconCheck = styled(IconCheck)`
  margin-right: 6px;
`

const Plan = ({ isActive, children }) => (
  <PlanContainer isActive={isActive}>
    {isActive && (
      <StyledIconCheck fill={theme.colors.greens[50]} width={8} height={8} />
    )}
    {children}
  </PlanContainer>
)

Plan.propTypes = {
  isActive: PropTypes.bool,
  children: PropTypes.node,
}

const BundleHeroSubSection = ({
  name,
  title,
  description,
  coverImage,
  youtubeId,
}) => {
  const { eid } = useAnvilUser() || {}
  const isUserLoggedInToAnvil = !!eid

  const renderAsset = () => {
    if (!coverImage) return null
    if (youtubeId) {
      return (
        <AssetContainer>
          <VideoModal
            title={title}
            description={description}
            videoId={youtubeId}
            renderPlaceholder={({ onClick }) => {
              return (
                <VideoAssetContainer onClick={onClick}>
                  <VideoAssetInner>
                    <PlayButton />
                    <StyledVideoAssetPlaceholderImg
                      image={getImage(coverImage.asset.gatsbyImageData)}
                      alt={title}
                    />
                  </VideoAssetInner>
                </VideoAssetContainer>
              )
            }}
          />
        </AssetContainer>
      )
    }
    return (
      <AssetContainer>
        <StyledVideoAssetPlaceholderImg
          image={getImage(coverImage.asset.gatsbyImageData)}
          alt={title}
        />
      </AssetContainer>
    )
  }

  return (
    <SubSection background={theme.colors.gray}>
      <HeroContent>
        <TextContainer noCover={!coverImage}>
          <Title>{title}</Title>
          <Description>{description}</Description>
          <CtaWrapper>
            <Cta
              type="black"
              chevron={{ position: 'inner', direction: 'right' }}
              size="large"
              to={
                isUserLoggedInToAnvil ? 'contact-support' : `${APP_URL}/signup`
              }
            >
              {isUserLoggedInToAnvil
                ? `Talk to us about ${name}`
                : 'Sign up to use'}
            </Cta>
          </CtaWrapper>
        </TextContainer>
        {renderAsset()}
      </HeroContent>
    </SubSection>
  )
}

BundleHeroSubSection.propTypes = {
  name: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  coverImage: PropTypes.object,
  youtubeId: PropTypes.string,
}

export default BundleHeroSubSection
